import { useMemo } from 'react';

import { LabelId, UserCommunication } from '@this+that/models';

import { VIEW_TYPES } from '../../../enums/viewTypes';
import {
  UserCommunicationFilter,
  useFilterUserCommunications,
} from '../../../hooks';
import { useLocalDraftEntries } from '../../../hooks/UserCommunications/draft/useLocalDraftEntries';
import { useUserCommunicationsStore } from '../../../store';
import { InboxType } from '../types';

export interface InboxCountResult {
  count: number;
  isPartial: boolean;
}

const EMPTY_INBOX_COUNT: InboxCountResult = { count: 0, isPartial: false };

const countUnreadMainMessages = (messages: UserCommunication[]): number => {
  const uniqueByMessageId = Array.from(
    new Map(messages.map((message) => [message.messageId, message])).values(),
  );

  return uniqueByMessageId.filter((communication) => {
    const hasInboxAndUnread =
      communication.labelIds?.includes(LabelId.INBOX) &&
      communication.labelIds?.includes(LabelId.UNREAD);

    return hasInboxAndUnread;
  }).length;
};

export const addLocalDraftCount = (
  serverCount: number,
  localCount: number,
  isDraftSynced: boolean,
): number => (isDraftSynced ? serverCount + localCount : serverCount);

export function useInboxCount(type: InboxType): InboxCountResult {
  const primary = useUserCommunicationsStore((state) => state.primary);
  const draft = useUserCommunicationsStore((state) => state.draft);
  const primaryStartKey = useUserCommunicationsStore(
    (state) => state.startKeys.primary,
  );
  const draftStartKey = useUserCommunicationsStore(
    (state) => state.startKeys.draft,
  );

  const isDraftSynced = useUserCommunicationsStore((state) =>
    state.syncedViewTypes.includes(VIEW_TYPES.DRAFT),
  );
  const localDraftCount = useLocalDraftEntries().length;

  const { filteredCommunications: draftMessages } = useFilterUserCommunications(
    draft,
    UserCommunicationFilter.DRAFT,
  );

  const mainCount = useMemo(() => countUnreadMainMessages(primary), [primary]);

  if (type === 'main') {
    return { count: mainCount, isPartial: primaryStartKey !== null };
  }

  if (type === 'draft') {
    return {
      count: addLocalDraftCount(
        draftMessages?.length ?? 0,
        localDraftCount,
        isDraftSynced,
      ),
      isPartial: draftStartKey !== null,
    };
  }

  return EMPTY_INBOX_COUNT;
}
